import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  listRecipients,
  createRecipient,
  updateRecipient,
  deleteRecipient,
  getSchedule,
  updateSchedule,
  getTwilioConfig,
  updateTwilioConfig,
  listWaLogs,
  sendAnalysis,
} from "@/lib/api";
import { toast } from "sonner";
import {
  X,
  Mail,
  MessageCircle,
  Trash2,
  Plus,
  Calendar,
  Loader2,
  Users,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Info,
  Zap,
} from "lucide-react";

const DAYS = ["Senin", "Selasa", "Rabu", "Kamis", "Jumat", "Sabtu", "Minggu"];

export default function RecipientsDialog({ module, onClose }) {
  const qc = useQueryClient();
  const [channel, setChannel] = useState("email"); // email | whatsapp
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("to");

  const { data: recipients = [], isLoading } = useQuery({
    queryKey: ["recipients", module.id],
    queryFn: () => listRecipients(module.id),
  });

  const { data: schedule } = useQuery({
    queryKey: ["schedule", module.id],
    queryFn: () => getSchedule(module.id),
  });

  const { data: twilioCfg } = useQuery({
    queryKey: ["twilio-config"],
    queryFn: getTwilioConfig,
    refetchInterval: 15000, // refresh device status every 15s
  });

  const { data: waLogs = [] } = useQuery({
    queryKey: ["wa-logs", module.id],
    queryFn: () => listWaLogs(module.id),
    refetchInterval: 8000, // refresh every 8s to catch webhook updates
  });

  const cfgMut = useMutation({
    mutationFn: (payload) => updateTwilioConfig(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["twilio-config"] });
    },
    onError: () => toast.error("Gagal simpan config"),
  });

  const addMut = useMutation({
    mutationFn: () => {
      const payload = { channel, name: name.trim() };
      if (channel === "email") {
        payload.email = email.trim();
        payload.role = role;
      } else {
        payload.phone = phone.trim();
      }
      return createRecipient(module.id, payload);
    },
    onSuccess: () => {
      toast.success("Penerima ditambahkan");
      setEmail("");
      setPhone("");
      setName("");
      qc.invalidateQueries({ queryKey: ["recipients", module.id] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Gagal menambahkan"),
  });

  const updateRoleMut = useMutation({
    mutationFn: ({ id, newRole }) => updateRecipient(id, newRole),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["recipients", module.id] });
    },
    onError: () => toast.error("Gagal update role"),
  });

  const delMut = useMutation({
    mutationFn: (id) => deleteRecipient(id),
    onSuccess: () => {
      toast.success("Penerima dihapus");
      qc.invalidateQueries({ queryKey: ["recipients", module.id] });
    },
    onError: () => toast.error("Gagal menghapus"),
  });

  const schedMut = useMutation({
    mutationFn: (cfg) => updateSchedule(module.id, cfg),
    onSuccess: () => {
      toast.success("Jadwal diperbarui");
      qc.invalidateQueries({ queryKey: ["schedule", module.id] });
    },
    onError: () => toast.error("Gagal update jadwal"),
  });

  const testSendMut = useMutation({
    mutationFn: () => sendAnalysis(module.id),
    onSuccess: (res) => {
      const sentCount = res?.sent?.length || 0;
      const failCount = res?.failed?.length || 0;
      if (sentCount > 0 && failCount === 0) {
        toast.success(`Terkirim ke ${sentCount} penerima ✓`, {
          description: "AI analisis sudah di-generate & dikirim via email/WA.",
        });
      } else if (sentCount > 0 && failCount > 0) {
        const firstErr = res.failed[0]?.error?.slice(0, 120) || "";
        toast.warning(`Terkirim sebagian: ${sentCount} OK, ${failCount} gagal`, {
          description: firstErr,
        });
      } else {
        const firstErr = res.failed?.[0]?.error?.slice(0, 200) || "Tidak ada yang terkirim";
        toast.error("Gagal kirim ke semua penerima", { description: firstErr });
      }
      qc.invalidateQueries({ queryKey: ["wa-logs", module.id] });
    },
    onError: (e) =>
      toast.error(e?.response?.data?.detail || "Gagal generate/kirim analisis"),
  });

  const updateSched = (patch) => {
    schedMut.mutate({
      module: module.id,
      frequency: schedule?.frequency || "off",
      hour: schedule?.hour ?? 8,
      day_of_week: schedule?.day_of_week ?? 1,
      ...patch,
    });
  };

  const canAdd = channel === "email" ? !!email.trim() : !!phone.trim();
  const emailRecipients = recipients.filter((r) => (r.channel || "email") === "email");
  const waRecipients = recipients.filter((r) => r.channel === "whatsapp");

  const roleStyles = {
    to: "bg-blue-50 text-blue-700 border-blue-300",
    cc: "bg-slate-100 text-slate-700 border-slate-300",
    bcc: "bg-amber-50 text-amber-700 border-amber-300",
  };

  return (
    <div
      className="fixed inset-0 z-[70] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="recipients-dialog"
    >
      <div
        className="bg-white border border-slate-200 w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between p-6 border-b border-slate-200">
          <div>
            <div className="mono text-[11px] uppercase tracking-widest text-slate-400 mb-1">
              Kelola Penerima · {module.name}
            </div>
            <h3 className="font-display font-black text-xl tracking-tight">
              Notifikasi Analisis AI
            </h3>
            <p className="text-xs text-slate-500 mt-1">
              Kirim laporan otomatis via <b>Email</b> dan/atau <b>WhatsApp</b>.
            </p>
          </div>
          <button
            onClick={onClose}
            data-testid="recipients-close"
            className="p-1.5 hover:bg-slate-100"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Channel tabs */}
          <div className="flex border border-slate-200">
            <button
              data-testid="channel-tab-email"
              onClick={() => setChannel("email")}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-semibold transition-colors ${
                channel === "email"
                  ? "bg-[#0047FF] text-white"
                  : "bg-white text-slate-600 hover:bg-slate-50"
              }`}
            >
              <Mail className="w-4 h-4" />
              Email
            </button>
            <button
              data-testid="channel-tab-whatsapp"
              onClick={() => setChannel("whatsapp")}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-semibold transition-colors ${
                channel === "whatsapp"
                  ? "bg-emerald-600 text-white"
                  : "bg-white text-slate-600 hover:bg-slate-50"
              }`}
            >
              <MessageCircle className="w-4 h-4" />
              WhatsApp
            </button>
          </div>

          {/* Add form */}
          <div>
            <div className="mono text-[10px] uppercase tracking-widest text-slate-400 mb-2 flex items-center gap-1.5">
              <Plus className="w-3 h-3" /> Tambah Penerima {channel === "email" ? "Email" : "WhatsApp"}
            </div>
            <div className="flex gap-2 flex-wrap">
              {channel === "email" ? (
                <>
                  <input
                    data-testid="recipient-email-input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="email@perusahaan.com"
                    className="flex-1 min-w-[180px] px-3 py-2 border border-slate-300 text-sm focus:outline-none focus:border-[#0047FF]"
                  />
                  <input
                    data-testid="recipient-name-input"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Nama"
                    className="w-28 px-3 py-2 border border-slate-300 text-sm focus:outline-none focus:border-[#0047FF]"
                  />
                  <select
                    data-testid="recipient-role-select"
                    value={role}
                    onChange={(e) => setRole(e.target.value)}
                    className="w-20 px-2 py-2 border border-slate-300 text-sm bg-white focus:outline-none focus:border-[#0047FF]"
                  >
                    <option value="to">To</option>
                    <option value="cc">CC</option>
                    <option value="bcc">BCC</option>
                  </select>
                </>
              ) : (
                <>
                  <input
                    data-testid="recipient-phone-input"
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+6281234567890 atau 081234567890"
                    className="flex-1 min-w-[220px] px-3 py-2 border border-slate-300 text-sm focus:outline-none focus:border-emerald-500"
                  />
                  <input
                    data-testid="recipient-name-input"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Nama"
                    className="w-32 px-3 py-2 border border-slate-300 text-sm focus:outline-none focus:border-emerald-500"
                  />
                </>
              )}
              <button
                data-testid="recipient-add-btn"
                onClick={() => canAdd && addMut.mutate()}
                disabled={!canAdd || addMut.isPending}
                className={`btn-sharp text-white text-sm font-semibold px-4 py-2 disabled:opacity-40 ${
                  channel === "email"
                    ? "bg-[#0047FF] hover:bg-[#0033CC]"
                    : "bg-emerald-600 hover:bg-emerald-700"
                }`}
              >
                {addMut.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Plus className="w-4 h-4" />
                )}
              </button>
            </div>
            {channel === "whatsapp" && (
              <div className="mt-3 space-y-2">
                {/* DEMO MODE TOGGLE */}
                <div
                  className={`text-[11px] border p-3 ${
                    twilioCfg?.demo_mode
                      ? "bg-violet-50 border-violet-300 text-violet-900"
                      : "bg-slate-50 border-slate-300 text-slate-700"
                  }`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex-1">
                      <div className="font-semibold mb-1 flex items-center gap-1">
                        <Zap className="w-3.5 h-3.5" /> Demo Mode
                        {twilioCfg?.demo_mode && (
                          <span className="mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 bg-violet-600 text-white ml-1">
                            AKTIF
                          </span>
                        )}
                      </div>
                      <div className="leading-snug">
                        {twilioCfg?.demo_mode
                          ? "Pesan WA disimulasikan (log tampil sebagai delivered). Cocok untuk demo/screenshot tanpa perlu device Fonnte aktif."
                          : "Aktifkan untuk mensimulasikan kirim WA tanpa benar-benar mengirim (device Fonnte tidak dipanggil). Flow UI tetap berjalan normal."}
                      </div>
                    </div>
                    <button
                      data-testid="demo-mode-toggle"
                      onClick={() =>
                        cfgMut.mutate(
                          { demo_mode: !twilioCfg?.demo_mode },
                          {
                            onSuccess: () =>
                              toast.success(
                                twilioCfg?.demo_mode
                                  ? "Demo Mode dimatikan — WA akan dikirim via Fonnte"
                                  : "Demo Mode diaktifkan — WA akan disimulasikan"
                              ),
                          }
                        )
                      }
                      disabled={cfgMut.isPending}
                      className={`relative shrink-0 w-11 h-6 border transition-colors ${
                        twilioCfg?.demo_mode
                          ? "bg-violet-600 border-violet-700"
                          : "bg-white border-slate-400"
                      }`}
                      aria-label="Toggle demo mode"
                    >
                      <span
                        className={`absolute top-0.5 w-4 h-4 bg-white border border-slate-300 transition-transform ${
                          twilioCfg?.demo_mode ? "translate-x-5" : "translate-x-0.5"
                        }`}
                      />
                    </button>
                  </div>
                </div>

                <div
                  className={`text-[11px] border p-3 ${
                    twilioCfg?.device_status === "connected"
                      ? "bg-emerald-50 border-emerald-300 text-emerald-900"
                      : "bg-amber-50 border-amber-200 text-amber-900"
                  }`}
                >
                  <div className="font-semibold mb-1 flex items-center gap-1">
                    <Info className="w-3.5 h-3.5" />
                    Fonnte WhatsApp Gateway
                    <span
                      className={`mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 ml-1 ${
                        twilioCfg?.device_status === "connected"
                          ? "bg-emerald-600 text-white"
                          : "bg-amber-600 text-white"
                      }`}
                      data-testid="fonnte-device-status"
                    >
                      {twilioCfg?.device_status === "connected"
                        ? "TERSAMBUNG"
                        : twilioCfg?.device_status === "check_failed"
                        ? "GAGAL CEK"
                        : twilioCfg?.device_status === "unknown"
                        ? "PERLU DICEK"
                        : "TERPUTUS"}
                    </span>
                  </div>
                  {twilioCfg?.device_status === "connected" ? (
                    <div>
                      Device siap kirim WA{" "}
                      {twilioCfg?.whatsapp_from && (
                        <span className="mono text-[10px]">
                          dari {twilioCfg.whatsapp_from}
                        </span>
                      )}
                      . Penerima <b>tidak perlu opt-in</b> — pesan langsung
                      masuk ke WA mereka.
                    </div>
                  ) : (
                    <div>
                      Device Fonnte belum tersambung. Buka{" "}
                      <a
                        href="https://md.fonnte.com/device"
                        target="_blank"
                        rel="noreferrer"
                        className="underline font-semibold"
                      >
                        dashboard Fonnte → Device
                      </a>{" "}
                      → klik <b>Connect</b> → scan QR dengan WhatsApp Anda.
                      Setelah tersambung, tutup dialog ini & buka lagi untuk
                      refresh status.
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* List */}
          <div>
            <div className="mono text-[10px] uppercase tracking-widest text-slate-400 mb-2 flex items-center gap-1.5">
              <Users className="w-3 h-3" />
              {recipients.length} penerima ({emailRecipients.length} email, {waRecipients.length} WA)
            </div>
            {isLoading ? (
              <div className="text-xs text-slate-400 py-4">Memuat…</div>
            ) : recipients.length === 0 ? (
              <div className="text-xs text-slate-500 border border-dashed border-slate-300 p-6 text-center">
                Belum ada penerima.
              </div>
            ) : (
              <div className="space-y-1.5">
                {recipients.map((r) => {
                  const isWa = r.channel === "whatsapp";
                  const currentRole = r.role || "to";
                  return (
                    <div
                      key={r.id}
                      data-testid={`recipient-${r.id}`}
                      className="flex items-center gap-3 p-3 border border-slate-200 bg-white hover:border-slate-400"
                    >
                      <div className={`w-8 h-8 flex items-center justify-center shrink-0 ${
                        isWa ? "bg-emerald-50 text-emerald-700" : "bg-blue-50 text-blue-700"
                      }`}>
                        {isWa ? <MessageCircle className="w-4 h-4" /> : <Mail className="w-4 h-4" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">
                          {isWa ? r.phone : r.email}
                        </div>
                        {r.name && (
                          <div className="text-xs text-slate-500 truncate">
                            {r.name}
                          </div>
                        )}
                      </div>
                      {!isWa && (
                        <select
                          data-testid={`recipient-role-${r.id}`}
                          value={currentRole}
                          onChange={(e) =>
                            updateRoleMut.mutate({ id: r.id, newRole: e.target.value })
                          }
                          className={`text-[11px] font-bold uppercase tracking-wider px-2 py-1 border cursor-pointer ${roleStyles[currentRole]}`}
                        >
                          <option value="to">TO</option>
                          <option value="cc">CC</option>
                          <option value="bcc">BCC</option>
                        </select>
                      )}
                      {isWa && (
                        <span className="text-[11px] font-bold uppercase tracking-wider px-2 py-1 border border-emerald-300 bg-emerald-50 text-emerald-700">
                          WA
                        </span>
                      )}
                      <button
                        data-testid={`recipient-delete-${r.id}`}
                        onClick={() => {
                          if (window.confirm(`Hapus ${isWa ? r.phone : r.email}?`))
                            delMut.mutate(r.id);
                        }}
                        className="p-1.5 border border-slate-300 hover:border-rose-500 hover:text-rose-500"
                      >
                        <Trash2 className="w-3.5 h-3.5 text-slate-600" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* WA Delivery Logs */}
          {waLogs.length > 0 && (
            <div className="border-t border-slate-200 pt-5" data-testid="wa-logs">
              <div className="mono text-[10px] uppercase tracking-widest text-slate-400 mb-2 flex items-center gap-1.5">
                <MessageCircle className="w-3 h-3" /> Riwayat Pengiriman WA
                <span className="text-slate-500">({waLogs.length} pesan)</span>
              </div>
              <div className="space-y-1 max-h-72 overflow-y-auto">
                {waLogs.slice(0, 20).map((log) => {
                  const s = (log.status || "unknown").toLowerCase();
                  const styles = {
                    delivered: { bg: "bg-emerald-50 text-emerald-700 border-emerald-300", icon: CheckCircle2 },
                    read: { bg: "bg-emerald-50 text-emerald-700 border-emerald-300", icon: CheckCircle2 },
                    sent: { bg: "bg-blue-50 text-blue-700 border-blue-300", icon: CheckCircle2 },
                    queued: { bg: "bg-slate-100 text-slate-700 border-slate-300", icon: Clock },
                    accepted: { bg: "bg-slate-100 text-slate-700 border-slate-300", icon: Clock },
                    failed: { bg: "bg-rose-50 text-rose-700 border-rose-300", icon: XCircle },
                    undelivered: { bg: "bg-rose-50 text-rose-700 border-rose-300", icon: XCircle },
                  };
                  const st = styles[s] || {
                    bg: "bg-slate-100 text-slate-600 border-slate-300",
                    icon: AlertCircle,
                  };
                  const StIcon = st.icon;
                  const errMsg = log.error_message || "";
                  const errCode = log.error_code || "";
                  // Diagnose common Fonnte errors
                  let hint = "";
                  const lowerErr = errMsg.toLowerCase();
                  if (errCode === "disconnected_device" || lowerErr.includes("disconnect")) {
                    hint =
                      "💡 Device Fonnte belum tersambung. Scan QR di dashboard Fonnte → Device → Connect.";
                  } else if (errCode === "invalid_token" || lowerErr.includes("token")) {
                    hint =
                      "💡 Token Fonnte tidak valid. Salin ulang token dari dashboard Fonnte & update FONNTE_TOKEN di .env.";
                  } else if (errCode === "quota_exceeded" || lowerErr.includes("quota") || lowerErr.includes("limit")) {
                    hint =
                      "💡 Kuota Fonnte habis. Upgrade paket atau tunggu reset kuota.";
                  } else if (errCode === "invalid_target" || lowerErr.includes("target") || lowerErr.includes("number")) {
                    hint = "💡 Format nomor tidak valid. Pastikan pakai format 08... atau 62...";
                  } else if (errCode === "not_registered") {
                    hint = "💡 Nomor tujuan tidak terdaftar di WhatsApp.";
                  } else if (errCode === "network_error") {
                    hint = "💡 Gagal koneksi ke server Fonnte — cek internet backend.";
                  }
                  return (
                    <div
                      key={log.sid || log.id}
                      data-testid={`wa-log-${log.id}`}
                      className="border border-slate-200 bg-white text-xs"
                    >
                      <div className="flex items-center gap-3 p-2">
                        <StIcon className="w-4 h-4 text-slate-500 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">
                            {log.phone} {log.name && `· ${log.name}`}
                          </div>
                          <div className="mono text-[10px] text-slate-500">
                            {new Date(log.sent_at).toLocaleString("id-ID")}
                            {errCode && ` · code ${errCode}`}
                          </div>
                        </div>
                        <span
                          className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 border ${st.bg}`}
                        >
                          {s}
                        </span>
                      </div>
                      {errMsg && (
                        <div className="border-t border-slate-200 bg-rose-50/40 px-3 py-2">
                          <div className="text-[11px] text-rose-800 break-words leading-snug">
                            <span className="font-semibold">Error:</span> {errMsg}
                          </div>
                          {hint && (
                            <div className="text-[11px] text-slate-700 mt-1.5 bg-amber-50 border border-amber-200 px-2 py-1.5 leading-snug">
                              {hint}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="mono text-[10px] text-slate-400 mt-2">
                Status update otomatis dari Twilio webhook · refresh tiap 8 detik
              </div>
            </div>
          )}

          {/* Schedule */}
          <div className="border-t border-slate-200 pt-5">
            <div className="mono text-[10px] uppercase tracking-widest text-slate-400 mb-2 flex items-center gap-1.5">
              <Clock className="w-3 h-3" /> Jadwal Otomatis (WIB)
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Frekuensi</label>
                <select
                  data-testid="schedule-frequency"
                  value={schedule?.frequency || "off"}
                  onChange={(e) => updateSched({ frequency: e.target.value })}
                  className="w-full px-2 py-2 border border-slate-300 text-sm bg-white focus:outline-none focus:border-[#0047FF]"
                >
                  <option value="off">Nonaktif</option>
                  <option value="daily">Harian</option>
                  <option value="weekly">Mingguan</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Jam</label>
                <select
                  data-testid="schedule-hour"
                  value={schedule?.hour ?? 8}
                  onChange={(e) => updateSched({ hour: parseInt(e.target.value) })}
                  disabled={schedule?.frequency === "off"}
                  className="w-full px-2 py-2 border border-slate-300 text-sm bg-white focus:outline-none focus:border-[#0047FF] disabled:bg-slate-50 disabled:text-slate-400"
                >
                  {Array.from({ length: 24 }, (_, h) => (
                    <option key={h} value={h}>
                      {h.toString().padStart(2, "0")}:00
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Hari</label>
                <select
                  data-testid="schedule-day"
                  value={schedule?.day_of_week ?? 1}
                  onChange={(e) =>
                    updateSched({ day_of_week: parseInt(e.target.value) })
                  }
                  disabled={schedule?.frequency !== "weekly"}
                  className="w-full px-2 py-2 border border-slate-300 text-sm bg-white focus:outline-none focus:border-[#0047FF] disabled:bg-slate-50 disabled:text-slate-400"
                >
                  {DAYS.map((d, i) => (
                    <option key={i} value={i}>
                      {d}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {schedule?.last_sent && (
              <div className="mono text-[10px] text-slate-500 mt-2 flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                Terakhir dikirim: {new Date(schedule.last_sent).toLocaleString("id-ID")}
              </div>
            )}
            {schedule?.frequency !== "off" && (
              <div className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 p-2 mt-3">
                ✓ Digest {schedule?.frequency === "daily" ? "harian" : `mingguan (${DAYS[schedule?.day_of_week || 0]})`}{" "}
                akan dikirim jam {(schedule?.hour ?? 8).toString().padStart(2, "0")}:00 WIB
                ke {emailRecipients.length} email + {waRecipients.length} WA.
              </div>
            )}

            {/* Test Kirim Sekarang */}
            <div className="mt-4 pt-4 border-t border-dashed border-slate-200">
              <div className="mono text-[10px] uppercase tracking-widest text-slate-400 mb-2 flex items-center gap-1.5">
                <Zap className="w-3 h-3" /> Uji Coba Manual
              </div>
              <div className="flex items-start gap-3">
                <button
                  data-testid="test-send-now-btn"
                  onClick={() => {
                    if (recipients.length === 0) {
                      toast.error("Tambahkan minimal 1 penerima dulu");
                      return;
                    }
                    if (
                      !window.confirm(
                        `Generate analisis AI & kirim SEKARANG ke ${recipients.length} penerima?\n\n` +
                        `Ini butuh ~30 detik (AI perlu generate laporan lengkap).`
                      )
                    )
                      return;
                    testSendMut.mutate();
                  }}
                  disabled={testSendMut.isPending || recipients.length === 0}
                  className="btn-sharp bg-slate-900 text-white text-sm font-semibold px-4 py-2.5 hover:bg-slate-700 disabled:opacity-40 flex items-center gap-2 shrink-0"
                >
                  {testSendMut.isPending ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Mengirim…
                    </>
                  ) : (
                    <>
                      <Zap className="w-4 h-4" />
                      Test Kirim Sekarang
                    </>
                  )}
                </button>
                <div className="text-[11px] text-slate-500 leading-relaxed">
                  Kirim analisis AI ke semua penerima terdaftar tanpa menunggu jadwal.
                  Berguna untuk verifikasi konfigurasi email & WhatsApp.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
